import React, {createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState} from "react";
import {flushSync} from "react-dom";
import {useQueryClient} from "react-query";
import {User} from "../pages/settings/users/useUser";
import type {Facility} from "../pages/settings/facilities/useFacility";
import {isAuthPublicPath, redirectToLogin} from "../helpers/redirectToLogin";
import axiosClient from "../api/axios-client";
import {API_VALIDATION_ERROR_EVENT} from "../api/useApi";
import { applyLocale } from "./localeBridge";
import {PermissionProvider} from "./PermissionContext";
import {disconnectEcho, leaveFacilityChannel} from "../realtime/echo";
import {
    clearFacilityScopedStorage,
    clearSessionScopedStorage,
    SELECTED_FACILITY_STORAGE_KEY,
} from "../helpers/sessionScopedStorage";
import {resolveViewpointFacility} from "../helpers/viewpointFacility";

// Clés localStorage pour persister le facility sélectionné et l'utilisateur
const SELECTED_FACILITY_KEY = SELECTED_FACILITY_STORAGE_KEY;
const CURRENT_USER_KEY = 'CURRENT_USER_ID';

// Interfaces pour les données du contexte
interface Notification {
    success?: string;
    error?: string;
    warning?: string;
    info?: string;
}

interface IContextState {
    user: User;
    token: string | null;
    notifications: Notification | null;
    isUserLoading: boolean;
    isFacilitySwitching: boolean;
    currentFacility: Facility | null;
    setUser: (user: User) => void;
    setToken: (token: string | null) => void;
    setNotifications: (notifications: Notification | null) => void;
    setCurrentFacility: (facility: Facility | null) => void;
}

// Définir une valeur par défaut complète
export const defaultContextState: IContextState = {
    user: {
        id: 0,
        gender_id: 1,
        firstname: "John",
        lastname: "Doe",
        email: "john.doe@example.com",
        facility_id: 0,
        is_admin: false,
        is_super_admin: false,
        can_access_crm_prospects: false,
        can_access_crm_clients: false,
        facility: { id: 0,},
        customer_id: null,
        is_loyalty: false,
        loyalty_program_id: null,
        loyalty_points: 0,
        loyalty_level_points: 0,
        roles: [],
        permissions: [],
    },
    token: null,
    notifications: null,
    isUserLoading: false,
    isFacilitySwitching: false,
    currentFacility: null,
    setUser: () => {},
    setToken: () => {},
    setNotifications: () => {},
    setCurrentFacility: () => {},
};

// Type pour les enfants dans les props du fournisseur de contexte
type ContextProviderProps = {
    children: ReactNode;
};

// Contexte avec la valeur par défaut spécifiée
const StateContext = createContext<IContextState>(defaultContextState);

// Fournisseur de contexte qui utilise le hook useState pour gérer l'utilisateur et les tokens
export const ContextProvider = ({ children }: ContextProviderProps) => {
    const queryClient = useQueryClient();

    const [user, setUser] = useState<User>(defaultContextState.user);
    const [token, setToken] = useState<string | null>(localStorage.getItem('ACCESS_TOKEN'));
    const [notifications, _setNotifications] = useState<Notification|null>(null);
    const [isUserLoading, setIsUserLoading] = useState(false);
    const [isFacilitySwitching, setIsFacilitySwitching] = useState(false);
    const [currentFacility, _setCurrentFacility] = useState<Facility | null>(null);
    const notificationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        // Vérifie si l'utilisateur est sur une route publique. Source unique :
        // `AUTH_PUBLIC_ROUTES` (helpers/redirectToLogin.ts) — une liste locale
        // dupliquée ici a déjà dérivé de la vraie liste par le passé (#23).
        const currentPath = window.location.pathname;

        // Redirige uniquement si pas de token et route non publique
        if (!token && !isAuthPublicPath(currentPath)) {
            redirectToLogin();
        }
    }, [token]);

    useEffect(() => {
        if (!token) {
            setUser(defaultContextState.user);
            _setCurrentFacility(null);
            return;
        }
        setIsUserLoading(true);
        axiosClient.get('/user')
            .then(async ({data}) => {
                setUser(data);

                // Synchroniser la langue de l'UI admin avec la préférence user.
                //
                // Passe par le PONT et non par un import direct d'i18next : ce
                // module est partagé avec l'app mobile, et importer `../i18n` ici
                // y tirait 608 Ko de traductions en 24 requêtes, pour des écrans
                // qu'elle ne porte pas (#99). Voir `localeBridge.ts`.
                if (data.locale) {
                    applyLocale(data.locale, data.app_locale);
                }

                // Vérifier si l'utilisateur a changé (nouvelle session)
                const savedUserId = localStorage.getItem(CURRENT_USER_KEY);
                const currentUserId = data.id?.toString();

                if (savedUserId && savedUserId !== currentUserId) {
                    // L'utilisateur a changé, on nettoie le facility sauvegardé
                    localStorage.removeItem(SELECTED_FACILITY_KEY);
                }

                // Sauvegarder l'ID de l'utilisateur courant
                if (currentUserId) {
                    localStorage.setItem(CURRENT_USER_KEY, currentUserId);
                }

                // Récupérer le facility ID sauvegardé dans localStorage
                const savedFacilityId = localStorage.getItem(SELECTED_FACILITY_KEY);

                // Si un facility est sauvegardé et différent du facility de l'user
                if (savedFacilityId && data.facility && savedFacilityId !== data.facility.id.toString()) {
                    try {
                        // Essayer de récupérer le facility sauvegardé
                        const facilityResponse = await axiosClient.get(`/facilities/${savedFacilityId}`);
                        _setCurrentFacility(facilityResponse.data);
                    } catch (error) {
                        // Si le facility n'est plus accessible (403 ou autre), utiliser celui de l'user
                        console.warn('Saved facility no longer accessible, using user facility');
                        localStorage.removeItem(SELECTED_FACILITY_KEY);
                        if (data.facility) {
                            _setCurrentFacility(data.facility);
                        }
                    }
                } else if (data.facility) {
                    // Pas de facility sauvegardé ou identique à celui de l'user
                    _setCurrentFacility(data.facility);
                }
            })
            .catch((error) => {
                // Uniquement sur un vrai 401 (token invalide) : purge complète + retour login.
                // Erreur réseau / 5xx (backend indisponible) : on ne déconnecte pas, sinon le
                // token resté en localStorage provoque une boucle GuestLayout ↔ redirectToLogin.
                if (error?.response?.status === 401) {
                    handleSetToken(null);
                    redirectToLogin();
                }
            })
            .finally(() => setIsUserLoading(false));
    }, [token]);

    const setNotifications = useCallback((notifications: Notification|null) => {
        _setNotifications(notifications);
        // Reset le timer précédent pour éviter qu'un timer ancien clear la nouvelle notif trop tôt.
        if (notificationTimerRef.current) {
            clearTimeout(notificationTimerRef.current);
        }
        // Durée plus longue pour les erreurs et avertissements (messages souvent plus longs à lire)
        const duration = (notifications?.error || notifications?.warning) ? 6000 : 2000;
        notificationTimerRef.current = setTimeout(() => {
            _setNotifications(null);
            notificationTimerRef.current = null;
        }, duration);
    }, []);

    // Relais global des erreurs 422 émises par useApiMutation (opt-in via showValidationErrorToast).
    useEffect(() => {
        const handleValidationError = (event: Event) => {
            const message = (event as CustomEvent<{ message?: string }>).detail?.message;
            if (message) {
                setNotifications({ error: message });
            }
        };
        window.addEventListener(API_VALIDATION_ERROR_EVENT, handleValidationError);
        return () => window.removeEventListener(API_VALIDATION_ERROR_EVENT, handleValidationError);
    }, [setNotifications]);

    const handleSetUser = (user: User) => {
        setUser(user);
    };

    // Point UNIQUE de nettoyage au changement de jeton : connexion, SSO, les
    // écrans « se connecter en tant que » et la déconnexion passent tous ici,
    // alors que plusieurs contournent useAuth.
    const handleSetToken = (newToken: string | null) => {
        const isNewSession = newToken !== token;
        setToken(newToken);
        if (newToken) {
            localStorage.setItem('ACCESS_TOKEN', newToken);
        } else {
            localStorage.removeItem('ACCESS_TOKEN');
            localStorage.removeItem(CURRENT_USER_KEY);
        }

        if (isNewSession || !newToken) {
            clearSessionScopedStorage();
            disconnectEcho();
            queryClient.clear();
            _setCurrentFacility(null);
        }
    };

    const handleSetCurrentFacility = async (facility: Facility | null) => {
        const previousFacilityId = currentFacility?.id;

        // L'en-tête X-Facility-Id est lu ici par axios : l'écrire en premier.
        if (facility) {
            localStorage.setItem(SELECTED_FACILITY_KEY, facility.id.toString());
        } else {
            localStorage.removeItem(SELECTED_FACILITY_KEY);
        }

        if (!facility || previousFacilityId === facility.id) {
            _setCurrentFacility(facility);
            return;
        }

        // Ordre voulu : invalider seul laissait les écrans montés (badges,
        // crédits) sur l'hôtel précédent, et une réponse en vol partie avec
        // l'ancien en-tête pouvait remplir le cache du nouveau.
        setIsFacilitySwitching(true);
        try {
            if (previousFacilityId) {
                leaveFacilityChannel(previousFacilityId);
            }
            clearFacilityScopedStorage();
            await queryClient.cancelQueries();
            queryClient.removeQueries({inactive: true});
            // Rendu synchrone AVANT la remise à zéro : les écrans passent sur
            // les clés du nouvel établissement. Sinon une requête indexée sur
            // l'ancien id se rechargerait avec le nouvel en-tête et rangerait
            // les données du nouvel hôtel sous la clé de l'ancien.
            flushSync(() => _setCurrentFacility(facility));
            await queryClient.resetQueries({active: true});
        } finally {
            setIsFacilitySwitching(false);
        }
    };

    return (
        <StateContext.Provider value={{
            user,
            token,
            notifications,
            isUserLoading,
            isFacilitySwitching,
            currentFacility,
            setUser: handleSetUser,
            setToken: handleSetToken,
            setNotifications,
            setCurrentFacility: handleSetCurrentFacility,
        }}>
            <PermissionProvider
                permissions={user.permissions || []}
                roles={user.roles || []}
                isSuperAdmin={Boolean(user.is_super_admin)}
                isAdmin={Boolean(user.is_admin)}
                isOnGroupFacility={Boolean(resolveViewpointFacility(user, currentFacility)?.is_group)}
            >
                {children}
            </PermissionProvider>
        </StateContext.Provider>
    );
};


// Hook personnalisé pour utiliser le contexte
export const useStateContext = () => useContext(StateContext);
